import React, { useState } from 'react';
import { useAssessment } from '../../context/AssessmentContext';
import { ASSESSMENT_MODULES } from '../../config/assessmentConstants';
import styles from '../../styles/assessmentFlow.module.css';

const ModuleQuestions: React.FC = () => {
  const { state, dispatch } = useAssessment();
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
  const [moduleAnswers, setModuleAnswers] = useState<{ [key: string]: { [key: string]: boolean } }>({});

  const selectedModules = state.modules_selected;
  const currentModuleId = selectedModules[currentModuleIndex];
  const currentModule = ASSESSMENT_MODULES.find(m => m.id === currentModuleId);
  
  if (!currentModule) {
    return <div>Error: Module not found</div>;
  }

  const currentAnswers = moduleAnswers[currentModuleId] || {};
  const isCurrentModuleComplete = currentModule.questions.every(q => currentAnswers[q.id] !== undefined);
  const progress = ((currentModuleIndex + 1) / selectedModules.length) * 100;

  const handleAnswer = (questionId: string, answer: boolean) => {
    const updatedAnswers = {
      ...moduleAnswers,
      [currentModuleId]: {
        ...moduleAnswers[currentModuleId],
        [questionId]: answer
      }
    };
    
    setModuleAnswers(updatedAnswers);
    
    // Auto-advance to next question after a short delay
    setTimeout(() => {
      const nextQuestionIndex = currentModule.questions.findIndex(q => q.id === questionId) + 1;
      if (nextQuestionIndex < currentModule.questions.length) {
        // Move to next question in current module
        // No need to do anything - the component will re-render with the next question
      } else {
        // If this is the last question in the current module, save and move to next module
        dispatch({ 
          type: 'SET_MODULE_ANSWERS', 
          payload: { 
            module: currentModuleId, 
            answers: updatedAnswers[currentModuleId]
          } 
        });
        
        // Auto-advance to next module or finish
        if (currentModuleIndex < selectedModules.length - 1) {
          setCurrentModuleIndex(prev => prev + 1);
        }
        // If this was the last module, the context will automatically move to results
      }
    }, 300); // 300ms delay for smooth transition
  };

  const handleNext = () => {
    if (isCurrentModuleComplete) {
      // Save current module answers
      dispatch({ 
        type: 'SET_MODULE_ANSWERS', 
        payload: { 
          module: currentModuleId, 
          answers: currentAnswers 
        } 
      });

      // Move to next module or finish
      if (currentModuleIndex < selectedModules.length - 1) {
        setCurrentModuleIndex(prev => prev + 1);
      }
      // If this was the last module, the context will automatically move to results
    }
  };

  const handlePrevious = () => {
    if (currentModuleIndex > 0) {
      setCurrentModuleIndex(prev => prev - 1);
    }
  };

  const handleBackToModules = () => {
    dispatch({ type: 'SET_CURRENT_STEP', payload: 'modules' });
  };

  const getCurrentQuestionIndex = () => {
    const answeredCount = Object.keys(currentAnswers).length;
    return Math.min(answeredCount, currentModule.questions.length - 1);
  };

  const currentQuestionIndex = getCurrentQuestionIndex();
  const currentQuestion = currentModule.questions[currentQuestionIndex];

  return (
    <div className={styles.moduleQuestions}>
      <div className={styles.moduleHeader}>
        <h2>{currentModule.name} Questions</h2>
        <p>{currentModule.description}</p>
        
        <div className={styles.moduleProgress}>
          <div className={styles.progressBar}>
            <div 
              className={styles.progressFill} 
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className={styles.progressText}>
            Area {currentModuleIndex + 1} of {selectedModules.length}: {currentModule.name}
          </div>
        </div>
      </div>

      <div className={styles.questionContainer}>
        <div className={styles.questionHeader}>
          <div className={styles.questionNumber}>
            Question {currentQuestionIndex + 1} of {currentModule.questions.length}
          </div>
          <div className={styles.runningTotal}>
            {Object.keys(currentAnswers).length}/{currentModule.questions.length}
          </div>
        </div>
        
        <div className={styles.questionText}>
          {currentQuestion.text}
        </div>

        <div className={styles.answerOptions}>
          <label className={styles.answerOption}>
            <input
              type="radio"
              name={`${currentModuleId}-${currentQuestion.id}`}
              value="true"
              checked={currentAnswers[currentQuestion.id] === true}
              onChange={() => handleAnswer(currentQuestion.id, true)}
            />
            <span className={styles.answerText}>Yes</span>
          </label>
          
          <label className={styles.answerOption}>
            <input
              type="radio"
              name={`${currentModuleId}-${currentQuestion.id}`}
              value="false"
              checked={currentAnswers[currentQuestion.id] === false}
              onChange={() => handleAnswer(currentQuestion.id, false)}
            />
            <span className={styles.answerText}>No</span>
          </label>
        </div>

        <div className={styles.questionNavigation}>
          <button
            onClick={handlePrevious}
            disabled={currentModuleIndex === 0}
            className={styles.navButton}
            type="button"
          >
            Previous
          </button>
          
          <button
            onClick={handleNext}
            disabled={!isCurrentModuleComplete}
            className={styles.navButton}
            type="button"
          >
            Next
          </button>
        </div>
      </div>

      <div className={styles.moduleActions}>
        <button
          onClick={handleBackToModules}
          className={styles.backButton}
          type="button"
        >
          Back to Area Selection
        </button>
      </div>
    </div>
  );
};

export default ModuleQuestions;
